import { supabase } from '@/integrations/supabase/client';

// Custom error types
export class OpenAIError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OpenAIError';
  }
}

export class RateLimitError extends Error {
  constructor() {
    super('Rate limit exceeded. Please try again later.');
    this.name = 'RateLimitError';
  }
}

export class AuthenticationError extends Error {
  constructor() {
    super('Authentication required. Please sign in.');
    this.name = 'AuthenticationError';
  }
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

export const generateTaskFromTranscript = async (transcript: string) => {
  let attempts = 0;

  while (attempts < MAX_RETRIES) {
    try {
      // Validate input
      if (!transcript?.trim()) {
        throw new Error('No voice input provided');
      }

      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new AuthenticationError();
      }

      // Call Supabase Edge Function with the session token
      const { data, error } = await supabase.functions.invoke('process-voice-task', {
        body: { transcript: transcript.trim() },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      // Handle specific error cases
      if (error) {
        if (error.message?.includes('rate limit')) {
          throw new RateLimitError();
        }
        if (error.status === 401) {
          // If we get a 401, try to refresh the session
          const { data: { session: refreshedSession }, error: refreshError } = 
            await supabase.auth.refreshSession();
          
          if (refreshError || !refreshedSession) {
            throw new AuthenticationError();
          }

          // Retry with the new token
          const { data: retryData, error: retryError } = await supabase.functions.invoke('process-voice-task', {
            body: { transcript: transcript.trim() },
            headers: {
              Authorization: `Bearer ${refreshedSession.access_token}`
            }
          });

          if (retryError) {
            throw new OpenAIError('Failed to process voice input after token refresh');
          }

          return retryData.result;
        }
        console.error('Error calling process-voice-task:', error);
        throw new OpenAIError('Failed to process voice input');
      }

      if (!data?.result) {
        throw new OpenAIError('No response from task processing');
      }

      // Validate the response format
      const result = data.result;
      if (!result.title || !result.category || 
          !['Personal', 'Work', 'Health', 'Learning'].includes(result.category)) {
        throw new OpenAIError('Invalid task format received');
      }

      return result;
    } catch (error) {
      attempts++;
      
      // Don't retry on certain errors
      if (error instanceof RateLimitError || 
          error instanceof AuthenticationError) {
        throw error;
      }

      // On last attempt, throw the error
      if (attempts === MAX_RETRIES) {
        console.error('Max retries reached:', error);
        throw error;
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
    }
  }
}; 